import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Logo from '../ui/Logo';

interface FloatingHeaderProps {
  currentView: 'comics' | 'lists';
  onViewChange: (view: 'comics' | 'lists') => void;
  onMenuToggle: () => void;
  isMenuOpen: boolean;
  menuRef: React.RefObject<HTMLDivElement | null>;
  menuItems: React.ReactNode;
  // Search and filter props (optional)
  showSearch?: boolean;
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
  showFilters?: boolean;
  currentFilter?: 'all' | 'owned' | 'wishlist' | 'ordered' | 'loaned';
  onFilterChange?: (filter: 'all' | 'owned' | 'wishlist' | 'ordered' | 'loaned') => void;
  // List selector props (for lists view)
  lists?: Array<{ id: number; name: string }>;
  selectedListId?: number | null;
  onListChange?: (listId: number | null) => void;
}

const FloatingHeader: React.FC<FloatingHeaderProps> = ({
  currentView,
  onViewChange,
  onMenuToggle,
  isMenuOpen,
  menuRef,
  menuItems,
  showSearch = false,
  searchQuery = '',
  onSearchChange,
  showFilters = false,
  currentFilter = 'all',
  onFilterChange,
  lists = [],
  selectedListId = null,
  onListChange
}) => {
  const { t } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsScrolled(scrollTop > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleBackToComics = () => {
    onViewChange('comics');
    navigate("/");
  };

  const handleViewLists = () => {
    onViewChange('lists');
    navigate("/lists");
  };

  return (
    <header 
      className={`
        fixed top-0 left-0 right-0 z-50 
        bg-white/95 dark:bg-gray-800/95 
        backdrop-blur-sm shadow-sm
        transition-all duration-300 ease-in-out
        ${isScrolled ? 'py-2' : 'py-4'}
      `}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Desktop Layout - Single Row */}
        <div className="hidden min-[800px]:flex justify-between items-center gap-4">
          {/* Left Section: Logo + View Toggle */}
          <div className="flex items-center space-x-4 flex-shrink-0">
            {/* Logo */}
            <div 
              className={`
                transition-all duration-300 ease-in-out
                ${isScrolled ? 'scale-75' : 'scale-100'}
              `}
            >
              <Logo 
                height={isScrolled ? 32 : 40} 
                className="text-gray-900 dark:text-gray-100"
              />
            </div>

            {/* View Toggle */}
            <div className="flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
              <button
                onClick={handleBackToComics}
                className={`
                  px-3 py-1 rounded-md text-sm font-medium transition-colors
                  ${currentView === 'comics' 
                    ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-gray-100 shadow-sm' 
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                  }
                `}
              >
                {t('common.comics')}
              </button>
              <button
                onClick={handleViewLists}
                className={`
                  px-3 py-1 rounded-md text-sm font-medium transition-colors
                  ${currentView === 'lists' 
                    ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-gray-100 shadow-sm' 
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                  }
                `}
              >
                {t('common.lists')}
              </button>
            </div>
          </div>

          {/* Center Section: Search */}
          {showSearch && (
            <div className="flex items-center flex-1 max-w-2xl">
              {/* Search Input */}
              <div className="relative flex-1 min-w-0">
                <input
                  type="text"
                  placeholder={t('placeholder.searchComics')}
                  value={searchQuery}
                  onChange={(e) => onSearchChange?.(e.target.value)}
                  className={`
                    w-full px-3 pr-8 border border-gray-300 dark:border-gray-600 rounded-md 
                    bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 
                    placeholder-gray-500 dark:placeholder-gray-400 
                    focus:ring-blue-500 focus:border-blue-500 transition-all duration-300
                    ${isScrolled ? 'py-1.5 text-sm' : 'py-2'}
                  `}
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => onSearchChange?.('')}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 focus:outline-none"
                    aria-label={t('common.clear')}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Right Section: List Selector + Filter Buttons + Menu */}
          <div className="flex items-center gap-3 flex-shrink-0">
            {/* List Selector */}
            {currentView === 'lists' && lists.length > 0 && (
              <div className="relative flex-shrink-0">
                <select
                  value={selectedListId || ""}
                  onChange={(e) => {
                    const listId = e.target.value ? parseInt(e.target.value) : null;
                    onListChange?.(listId);
                  }}
                  className={`
                    px-3 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 
                    rounded-md text-sm text-gray-900 dark:text-gray-100 
                    focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-300
                    ${isScrolled ? 'py-1.5' : 'py-2'}
                  `}
                >
                  <option value="">{t('list.selectList')}</option>
                  {lists.map((list) => (
                    <option key={list.id} value={list.id}>
                      {list.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Filter Buttons */}
            {showFilters && (
              <div className="flex gap-1">
                {(['all', 'owned', 'wishlist', 'ordered', 'loaned'] as const).map((status) => (
                  <button
                    key={status}
                    onClick={() => onFilterChange?.(status)}
                    className={`
                      px-2 rounded-md capitalize transition-all duration-300 whitespace-nowrap
                      ${isScrolled ? 'py-1 text-xs' : 'py-1.5 text-sm'}
                      ${currentFilter === status
                        ? 'bg-blue-600 text-white shadow-sm'
                        : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                      }
                    `}
                  >
                    {status === 'all' ? t('common.all') : t(`status.${status}`)}
                  </button>
                ))}
              </div>
            )}

            {/* Menu */}
            <div className="relative" ref={menuRef}>
              <button
                onClick={onMenuToggle}
                className="p-2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                aria-label={t('common.menu')}
              >
                <svg
                  className={`transition-all duration-300 ${isScrolled ? 'h-5 w-5' : 'h-6 w-6'}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
              </button>

              {isMenuOpen && menuItems}
            </div>
          </div>

        </div>

        {/* Mobile/Tablet Layout - Two Rows */}
        <div className="min-[800px]:hidden">
          {/* First Row: Logo + View Toggle + Menu */}
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              {/* Logo */}
              <div 
                className={`
                  transition-all duration-300 ease-in-out
                  ${isScrolled ? 'scale-75' : 'scale-100'}
                `}
              >
                <Logo 
                  height={isScrolled ? 28 : 36} 
                  className="text-gray-900 dark:text-gray-100"
                />
              </div>

              {/* View Toggle */}
              <div className="flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                <button
                  onClick={handleBackToComics}
                  className={`
                    px-2.5 py-1 rounded-md text-sm font-medium transition-colors
                    ${currentView === 'comics' 
                      ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-gray-100 shadow-sm' 
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                    }
                  `}
                >
                  {t('common.comics')}
                </button>
                <button
                  onClick={handleViewLists}
                  className={`
                    px-2.5 py-1 rounded-md text-sm font-medium transition-colors
                    ${currentView === 'lists' 
                      ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-gray-100 shadow-sm' 
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                    }
                  `}
                >
                  {t('common.lists')}
                </button>
              </div>
            </div>

            {/* Menu */}
            <div className="relative" ref={menuRef}>
              <button
                onClick={onMenuToggle}
                className="p-2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                aria-label={t('common.menu')}
              >
                <svg
                  className={`transition-all duration-300 ${isScrolled ? 'h-5 w-5' : 'h-6 w-6'}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
              </button>

              {isMenuOpen && menuItems}
            </div>
          </div>

          {/* Second Row: Search + List Selector + Filters (Mobile) */}
          {(showSearch || (currentView === 'lists' && lists.length > 0) || showFilters) && (
            <div className="flex flex-col gap-3 mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
              
              {/* Search Input */}
              {showSearch && (
                <div className="relative flex-1 min-w-0">
                  <input
                    type="text"
                    placeholder={t('placeholder.searchComics')}
                    value={searchQuery}
                    onChange={(e) => onSearchChange?.(e.target.value)}
                    className={`
                      w-full px-3 pr-8 border border-gray-300 dark:border-gray-600 rounded-md 
                      bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 
                      placeholder-gray-500 dark:placeholder-gray-400 
                      focus:ring-blue-500 focus:border-blue-500 transition-all duration-300
                      ${isScrolled ? 'py-1.5 text-sm' : 'py-2'}
                    `}
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => onSearchChange?.('')}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 focus:outline-none"
                      aria-label={t('common.clear')}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              )}

              {/* Bottom row: List Selector + Filter Buttons */}
              {((currentView === 'lists' && lists.length > 0) || showFilters) && (
                <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
                  {/* List Selector */}
                  {currentView === 'lists' && lists.length > 0 && (
                    <div className="relative flex-shrink-0 sm:w-auto w-full">
                      <select
                        value={selectedListId || ""}
                        onChange={(e) => {
                          const listId = e.target.value ? parseInt(e.target.value) : null;
                          onListChange?.(listId);
                        }}
                        className={`
                          w-full px-3 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 
                          rounded-md text-sm text-gray-900 dark:text-gray-100 
                          focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-300
                          ${isScrolled ? 'py-1.5' : 'py-2'}
                        `}
                      >
                        <option value="">{t('list.selectList')}</option>
                        {lists.map((list) => (
                          <option key={list.id} value={list.id}>
                            {list.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Filter Buttons */}
                  {showFilters && (
                    <div className="flex gap-1 flex-wrap sm:justify-end justify-center">
                      {(['all', 'owned', 'wishlist', 'ordered', 'loaned'] as const).map((status) => (
                        <button
                          key={status}
                          onClick={() => onFilterChange?.(status)}
                          className={`
                            px-2 rounded-md capitalize transition-all duration-300 whitespace-nowrap
                            ${isScrolled ? 'py-1 text-xs' : 'py-1.5 text-sm'}
                            ${currentFilter === status
                              ? 'bg-blue-600 text-white shadow-sm'
                              : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                            }
                          `}
                        >
                          {status === 'all' ? t('common.all') : t(`status.${status}`)}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default FloatingHeader;